import React, {useEffect, useState, useRef, useCallback, } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap,} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useAuth } from "../../../shared/contexts/AuthContext";
import { useLocationContext } from "../../../shared/contexts/useLocationContext";
import { locationKey } from "../../../shared/location/locationKey";
import { useTranslation } from "react-i18next";
import HighLogo from "../../HighLogo";
import logo from "../../../assets/logo.png";
import MapInitializer from "../../../shared/location/MapInitializer";

type NominatimResult = {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  class?: string;
  type?: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    country?: string;
    country_code?: string;
  };
};

type PopularLocation = {
  label: string;
  city: string;
  country: string;
  lat: number;
  lng: number;
};

const POPULAR_LOCATIONS: PopularLocation[] = [
  {
    label: "London",
    city: "London",
    country: "United Kingdom",
    lat: 51.5074,
    lng: -0.1278,
  },
  {
    label: "Paris",
    city: "Paris",
    country: "France",
    lat: 48.8566,
    lng: 2.3522,
  },
  {
    label: "Vienna",
    city: "Vienna",
    country: "Austria",
    lat: 48.2082,
    lng: 16.3738,
  },
  {
    label: "Los Angeles",
    city: "Los Angeles",
    country: "United States",
    lat: 34.0522,
    lng: -118.2437,
  },
  {
    label: "New York",
    city: "New York",
    country: "United States",
    lat: 40.7128,
    lng: -74.006,
  },
  {
    label: "Madrid",
    city: "Madrid",
    country: "Spain",
    lat: 40.4168,
    lng: -3.7038,
  },
  {
    label: "São Paulo",
    city: "São Paulo",
    country: "Brazil",
    lat: -23.5505,
    lng: -46.6333,
  },
  {
    label: "Rio de Janeiro",
    city: "Rio de Janeiro",
    country: "Brazil",
    lat: -22.9068,
    lng: -43.1729,
  },
  {
    label: "Mexico City",
    city: "Mexico City",
    country: "Mexico",
    lat: 19.4326,
    lng: -99.1332,
  },
];

const ChangeLocationGeo = () => {
  const navigate = useNavigate();
  const locationHook = useLocation();
  const params = new URLSearchParams(locationHook.search);
  const { user } = useAuth();
  const { t } = useTranslation();

  const rawRedirect = params.get("from") || "/gallery";
  const redirectTo = rawRedirect.startsWith("/")
    ? rawRedirect
    : `/${rawRedirect}`;

    const UNKNOWN = t("locationChange.unknown", "Unknown");


  const handleClose = () => {
    sessionStorage.setItem("locchange:result", "unchanged");
    navigate(redirectTo);
  };

 const [location, setLocation] = useState<{
  city: string;
  country: string;
  countryCode?: string;
  coords: { lat: number; lng: number };
}>({
  city: UNKNOWN,
  country: UNKNOWN,
  countryCode: undefined,
  coords: { lat: 0, lng: 0 },
});

  // search state
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [localMatches, setLocalMatches] = useState<PopularLocation[]>([]);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const clickedToEditRef = useRef(false);

const { updateLocation, location: globalLoc } = useLocationContext();

  // ----- helpers -----
  


  const initialLocKeyRef = useRef<string>("");
  useEffect(() => {
    initialLocKeyRef.current = locationKey({
  city: globalLoc?.city,
  country: globalLoc?.country,
  countryCode: globalLoc?.countryCode,
});
    if (globalLoc?.city && globalLoc.city !== UNKNOWN) setQuery("");
    else if (globalLoc?.country && globalLoc.country !== UNKNOWN) setQuery("");
  }, [globalLoc]);

 const fetchCurrentLocation = useCallback(async () => {
  try {
    const updated = await updateLocation(undefined, undefined, true);

    if (updated?.coords && mapRef.current) {
      mapRef.current.flyTo(
        [updated.coords.lat, updated.coords.lng],
        5,
        { animate: true, duration: 1.5 }
      );
    }
  } catch (err) {
    console.warn("Error fetching current location:", err);
  }
}, [updateLocation]);


  const MapReferenceHandler = () => {
    const map = useMap();
    mapRef.current = map;
    return null;
  };

  // unified search

  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setLocalMatches([]);
      return;
    }
  
    // ignore spaces so "newy" matches "New York"
    const normalized = q.replace(/\s+/g, "");
  
    const matches = POPULAR_LOCATIONS.filter((pl) => {
      const labelNorm = pl.label.toLowerCase().replace(/\s+/g, "");
      return labelNorm.startsWith(normalized);
    });
  
    setLocalMatches(matches);
  }, [query]);

  
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    const ctrl = new AbortController();
    const tId = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=8&q=${encodeURIComponent(
          q
        )}`;
        const res = await fetch(url, {
  signal: ctrl.signal,
  headers: {
    'User-Agent': 'unydoapp/1.0',
    'Referer': window.location.origin,
  },
});

        const data: NominatimResult[] = await res.json();
        setResults(data);
        setOpen(data.length > 0);
        setActiveIndex(data.length ? 0 : -1);
      } catch (e: any) {
        if (e.name !== "AbortError") console.error("Search failed:", e);
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => {
      clearTimeout(tId);
      ctrl.abort();
    };
  }, [query]);

  // click outside dropdown
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!open) return;
      const tNode = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(tNode) &&
        inputRef.current &&
        !inputRef.current.contains(tNode)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pickResult = useCallback(
  async (r: NominatimResult) => {
    const lat = parseFloat(r.lat);
    const lng = parseFloat(r.lon);

    try {
      // single source of truth
      const updated = await updateLocation(lat, lng);
      setLocation(updated);

      mapRef.current?.flyTo([lat, lng], 12, {
        animate: true,
        duration: 1.2,
      });

      const short =
        updated.city !== UNKNOWN
          ? `${updated.city}, ${updated.country}`
          : r.display_name.split(",").slice(0, 2).join(", ");

      setQuery(short);
      setResults([]);
      setOpen(false);
      setActiveIndex(-1);
      inputRef.current?.blur();
    } catch (err) {
      console.error("Location pick failed:", err);
    }
  },
  [updateLocation, UNKNOWN]
);


  const GALLERY_HARD_RELOAD_KEY = "gallery:hardReload";

  const handlePinDragEnd = async (e: L.DragEndEvent) => {
  const marker = e.target as L.Marker;
  const { lat, lng } = marker.getLatLng();

  try {
    const updated = await updateLocation(lat, lng);
    setLocation(updated);

    mapRef.current?.flyTo([lat, lng], 13, {
      animate: true,
      duration: 1.5,
    });
  } catch (err) {
    console.error("Error updating location:", err);
  }
};


  const handleConfirmLocation = async () => {
    if (location.coords.lat !== 0 && location.coords.lng !== 0) {
      const newKey = locationKey({
  city: location.city,
  country: location.country,
  countryCode: location.countryCode,
});
      const changed = newKey !== initialLocKeyRef.current;

      const safeLocation = {
  city: location.city,
  country: location.country,
  countryCode: location.countryCode,
};

      if (changed) {
        await updateLocation(location.coords.lat, location.coords.lng, true);
        sessionStorage.setItem("selectedLocation", JSON.stringify(safeLocation));
        sessionStorage.setItem("locchange:result", "changed");
      } else {
        sessionStorage.setItem("locchange:result", "unchanged");
      }

      if (redirectTo.startsWith("/gallery")) {
        sessionStorage.setItem(GALLERY_HARD_RELOAD_KEY, "1");
        const bump = redirectTo.includes("?")
          ? `${redirectTo}&refresh=${Date.now()}`
          : `${redirectTo}?refresh=${Date.now()}`;
        navigate(bump, { replace: true });
      } else {
        navigate(redirectTo);
      }
    } else {
      console.warn("Location coordinates are invalid.");
    }
  };

const handleCurrentLocation = async () => {
  try {
    const updated = await updateLocation(undefined, undefined, true);

    if (updated?.coords) {
      setLocation({
        city: updated.city,
        country: updated.country,
        coords: updated.coords,
      });

      mapRef.current?.flyTo(
        [updated.coords.lat, updated.coords.lng],
        12,
        { animate: true, duration: 1.5 }
      );
    }
  } catch (err) {
    console.error("Error fetching current location:", err);
  }
};

  const MapClickHandler = () => {
  useMapEvents({
    click: async (e) => {
      const { lat, lng } = e.latlng;

      try {
        const updated = await updateLocation(lat, lng);
        setLocation(updated);

        mapRef.current?.flyTo(
          [updated.coords.lat, updated.coords.lng],
          13,
          {
            animate: true,
            duration: 1.5,
          }
        );
      } catch (err) {
        console.error("Error updating location from map click:", err);
      }
    },
  });
  return null;
};


  // load initial
  useEffect(() => {
    if (!user) return;
    const storedLocation = sessionStorage.getItem("selectedLocation");
    if (storedLocation) {
      try {
        const parsedLocation = JSON.parse(storedLocation);
        if (parsedLocation.city && parsedLocation.country) {
  setLocation((prev) => ({
    ...prev,
    city: parsedLocation.city,
    country: parsedLocation.country,
    countryCode: parsedLocation.countryCode,
  }));
}
      } catch (err) {
        console.warn("Error parsing stored location:", err);
      }
    } else {
  // Only center map; don't auto-request GPS
  mapRef.current?.flyTo([48.2082, 16.3738], 4); // fallback EU center
}

  }, [user]);

  const badgeFor = (r: NominatimResult) => {
    const tType = (r.type || "").toLowerCase();
    if (tType === "country") return t("locationChange.badge.country");
    if (tType === "state") return t("locationChange.badge.state");
    if (["city", "town"].includes(tType))
      return t("locationChange.badge.city");
    if (tType === "village") return t("locationChange.badge.village");
    if (tType === "suburb") return t("locationChange.badge.suburb");
    return r.type
      ? r.type[0].toUpperCase() + r.type.slice(1)
      : t("locationChange.badge.place");
  };

  const primaryLabel = (r: NominatimResult) => {
    const a = r.address || {};
    return (
      a.city ||
      a.town ||
      a.village ||
      a.state ||
      a.country ||
      r.display_name.split(",")[0]
    );
  };

  const handlePickPopular = async (loc: PopularLocation) => {
  try {
    const updated = await updateLocation(loc.lat, loc.lng);
    setLocation(updated);

    mapRef.current?.flyTo([loc.lat, loc.lng], 12, {
      animate: true,
      duration: 1.3,
    });

    const label = `${
      updated.city !== UNKNOWN ? updated.city : loc.city
    }, ${
      updated.country !== UNKNOWN ? updated.country : loc.country
    }`;

    setQuery(label);
    setResults([]);
    setOpen(false);
    setActiveIndex(-1);
  } catch (err) {
    console.error("Error updating location from popular pick:", err);
  }
};

  
  return (
    <div style={pageShellStyle}>
      {/* Header with logo + close */}
 <header style={pageHeaderStyle}>
  <div style={pageHeaderLeft}>
    <HighLogo
      src={logo}
      style={{ width: '12.2%', display: 'block' }}
      padding={0}
      onClick={() => navigate('/gallery')}
    />
  </div>

  <button
    onClick={handleClose}
    aria-label={t(
      "locationChange.actions.closeAria",
      "Close location picker"
    )}
    style={headerCloseButtonStyle}
  >
    ✖
  </button>
</header>

      <main style={pageMainStyle}>
        <div style={cardGridStyle}>
          {/* Sidebar: selected location + search + suggestions + actions */}
          <aside style={sidebarContainerStyle}>
            <div style={sidebarCardStyle}>
              <h2 style={sidebarHeadingStyle}>
                {t(
                  "locationChange.title",
                  "Change your location"
                )}
              </h2>

              {/* Selected location */}
              <div style={selectedLocationBoxStyle}>
                <div style={selectedLabelTitleStyle}>
                  {t("locationChange.labels.selectedLabel", "Selected")}
                </div>
                <div style={selectedLabelStyle}>
                  {t("locationChange.labels.selected", {
                    city: location.city,
                    country: location.country,
                  })}
                </div>
              </div>

              {/* Search */}
              <div style={overlaySearchWrapperStyle} ref={popoverRef}>
                <div style={{ position: "relative" }}>
                  <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => {
                        setOpen(true);
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        setOpen(false);
                        clickedToEditRef.current = false;
                      }, 120);
                    }}
                    onKeyDown={(e) => {
                      if (!open) {
                        if (
                          e.key === "ArrowDown" &&
                          results.length &&
                          clickedToEditRef.current
                        ) {
                          e.preventDefault();
                          setOpen(true);
                          setActiveIndex(0);
                        }
                        return;
                      }
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setActiveIndex((i) =>
                          results.length ? (i + 1) % results.length : -1
                        );
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setActiveIndex((i) =>
                          results.length
                            ? (i - 1 + results.length) % results.length
                            : -1
                        );
                      } else if (e.key === "Enter") {
                        e.preventDefault();
                        if (activeIndex >= 0 && results[activeIndex]) {
                          pickResult(results[activeIndex]);
                        }
                      } else if (e.key === "Escape") {
                        setOpen(false);
                      }
                    }}
                    onPointerDown={() => {
                      clickedToEditRef.current = true;
                    }}
                    placeholder={t(
                      "locationChange.search.placeholder",
                      "Search for a location"
                    )}
                    style={unifiedInputStyle}
                    aria-expanded={open}
                    aria-autocomplete="list"
                    aria-controls="geo-search-listbox"
                    role="combobox"
                  />
                  {query && (
                    <button
                      onClick={() => {
                        setQuery("");
                        setResults([]);
                        setOpen(false);
                        setActiveIndex(-1);
                        inputRef.current?.focus();
                      }}
                      style={clearBtnStyle}
                      aria-label={t(
                        "locationChange.search.clearAria",
                        "Clear search"
                      )}
                    >
                      ×
                    </button>
                  )}
                </div>

                {/* Dropdown */}
                {open && (
                  <div style={dropdownCardStyle}>
                    {loading && (
                      <div style={dropdownHintStyle}>
                        {t("locationChange.search.searching")}
                      </div>
                    )}
                    {!loading && results.length === 0 && localMatches.length === 0 && (
  <div style={dropdownHintStyle}>
    {t("locationChange.search.noResults")}
  </div>
)}
{!loading && (localMatches.length > 0 || results.length > 0) && (
  <ul
    id="geo-search-listbox"
    role="listbox"
    aria-label={t(
      "locationChange.search.ariaSuggestions",
      "Location suggestions"
    )}
    style={listStyle}
  >
    {/* 1) Popular / local matches first */}
    {localMatches.map((pl) => (
      <li
        key={`popular-${pl.label}`}
        role="option"
        aria-selected={false}
        onMouseDown={(e) => {
          e.preventDefault();
          handlePickPopular(pl);
        }}
        style={itemStyle}
        title={`${pl.city}, ${pl.country}`}
      >
        <div style={rowTopStyle}>
          <span style={nameStyle}>{pl.label}</span>
          <span style={badgeStyle}>
            {t("locationChange.badge.popular", "Popular")}
          </span>
        </div>
        <div style={subLabelStyle}>
          {pl.city}, {pl.country}
        </div>
      </li>
    ))}

    {/* 2) Nominatim results afterwards */}
    {results.map((r, i) => {
      const active = i === activeIndex;
      return (
        <li
          key={r.place_id}
          role="option"
          aria-selected={active}
          onMouseEnter={() => setActiveIndex(i)}
          onMouseDown={(e) => {
            e.preventDefault();
            pickResult(r);
          }}
          style={{
            ...itemStyle,
            ...(active ? itemActiveStyle : null),
          }}
          title={r.display_name}
        >
          <div style={rowTopStyle}>
            <span style={nameStyle}>{primaryLabel(r)}</span>
            <span style={badgeStyle}>{badgeFor(r)}</span>
          </div>
          <div style={subLabelStyle}>{r.display_name}</div>
        </li>
      );
    })}
  </ul>
)}

                  </div>
                )}
              </div>

              {/* Popular suggestions */}
              <div style={popularBoxStyle}>
                <div style={popularTitleStyle}>
                  {t(
                    "locationChange.popular.title",
                    "Quick suggestions"
                  )}
                </div>
                <div style={popularChipsWrapperStyle}>
                  {POPULAR_LOCATIONS.map((pl) => (
                    <button
                      key={pl.label}
                      type="button"
                      style={popularChipStyle}
                      onClick={() => handlePickPopular(pl)}
                    >
                      {pl.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* spacer pushes actions to bottom */}
              <div style={{ flex: 1 }} />



{/* Left bottom terms privacy help buttons */}
<div
  style={{
    position: "fixed",
    left: 18,
    bottom: 8,
    width: 230,
    zIndex: 50,
    fontSize: 10,
    color: "#555",
    lineHeight: 1.6,
    textAlign: "left",
    pointerEvents: "auto",
  }}
>
  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/helpsupportstart")}
  >
    {t("mainSidebar.footer.helpSupport")}
  </span>

  {" · "}

  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/legal/privacypolicy")}
  >
    {t("mainSidebar.footer.privacyPolicy")}
  </span>

  {" · "}

  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/legal/termsofuse")}
  >
    {t("mainSidebar.footer.termsOfUse")}
  </span>
</div>

              {/* Actions */}
              <div style={sidebarActionsStyle}>
                <button
                  onClick={handleConfirmLocation}
                  style={confirmButtonStyle}
                >
                  {t(
                    "locationChange.actions.confirm",
                    "Confirm location"
                  )}
                </button>

                <button
                  onClick={handleCurrentLocation}
                  style={currentLocationButtonStyle}
                >
                  {t(
                    "locationChange.actions.useCurrentLocation",
                    "Use current location"
                  )}
                </button>
              </div>
            </div>
          </aside>

          {/* Map area */}
          <section style={mapOuterStyle}>
            <div style={mapCardStyle}>
              <div style={mapWrapperStyle}>
                <MapContainer
                  center={[location.coords.lat, location.coords.lng]}
                  zoom={5}
                  style={mapStyle}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <MapReferenceHandler />
                  <MapInitializer
                    fetchCurrentLocation={fetchCurrentLocation}
                    setLocation={setLocation}
                  />
                  <MapClickHandler />
                  {location.coords.lat !== 0 &&
                    location.coords.lng !== 0 && (
                      <Marker
                        position={[
                          location.coords.lat,
                          location.coords.lng,
                        ]}
                        draggable
                        eventHandlers={{ dragend: handlePinDragEnd }}
                      />
                    )}
                </MapContainer>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

// ---------- Styles ----------

const pageShellStyle: React.CSSProperties = {
  minHeight: "100vh",
  height: "100vh",
  width: "100%",
  background:
    "radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)",
  color: "#e5e7eb",
  fontFamily:
    'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const pageHeaderStyle: React.CSSProperties = {
  position: 'sticky',
  top: '12px',
  left: '-12px',
  right: 0,
  zIndex: 30,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: 'transparent',
};

const pageHeaderLeft: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
};

const headerCloseButtonStyle: React.CSSProperties = {
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.6)",
  background: "rgba(15,23,42,0.95)",
  color: "#e5e7eb",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 34,
  height: 34,
  fontSize: 16,
};

const pageMainStyle: React.CSSProperties = {
  flex: 1,
  padding: "12px 24px 24px",
};

/* Grid: sidebar + map */
const cardGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "320px minmax(0, 1fr)",
  gap: 18,
  alignItems: "stretch",
  height: "calc(95vh - 80px)",
};

/* Sidebar container */
const sidebarContainerStyle: React.CSSProperties = {
  position: "sticky",
  top: 70,
  alignSelf: "flex-start",
  height: "calc(94vh - 78px)",
};

/* Sidebar card (similar vibe to Gallery sidebar) */
const sidebarCardStyle: React.CSSProperties = {
  height: "100%",
  background:
    "linear-gradient(145deg, rgba(4, 7, 15, 0.96), rgba(15,23,42,0.99))",
  color: "#e5e7eb",
  display: "flex",
  flexDirection: "column",
  gap: 14,
  borderRadius: 20,
  border: "1px solid rgba(148,163,184,0.35)",
  boxShadow: "0 22px 50px rgba(0,0,0,0.8)",
  padding: 18,
};

const sidebarHeadingStyle: React.CSSProperties = {
  marginBottom: 4,
  fontSize: 18,
  fontWeight: 600,
  letterSpacing: -0.02,
};

/* Selected location box */
const selectedLocationBoxStyle: React.CSSProperties = {
  background: "rgba(15,23,42,0.85)",
  borderRadius: 14,
  padding: "8px 10px",
  border: "1px solid rgba(148,163,184,0.4)",
};

const selectedLabelTitleStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.08,
  color: "#9ca3af",
  marginBottom: 4,
};

const selectedLabelStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: "#cbd5f5",
};

/* Search wrapper reused */
const overlaySearchWrapperStyle: React.CSSProperties = {
  position: "relative",
  marginTop: 8,
};

const unifiedInputStyle: React.CSSProperties = {
  width: "84%",
  padding: "10px 38px 10px 12px",
  borderRadius: 999,
  border: "1px solid #2a3444",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
  color: "#e5eefb",
  outline: "none",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
  fontSize: 14,
};

const clearBtnStyle: React.CSSProperties = {
  position: "absolute",
  right: 10,
  top: "50%",
  transform: "translateY(-50%)",
  border: "none",
  background: "transparent",
  color: "#cbd5e1",
  fontSize: 18,
  cursor: "pointer",
};

/* Dropdown under search */
const dropdownCardStyle: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 6px)",
  left: 0,
  right: 0,
  background: "#0b0f17",
  border: "1px solid #22304a",
  borderRadius: 10,
  boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
  zIndex: 2000,
  maxHeight: 320,
  overflow: "hidden",
};

const listStyle: React.CSSProperties = {
  listStyle: "none",
  padding: 0,
  margin: 0,
  maxHeight: 320,
  overflowY: "auto",
};

const itemStyle: React.CSSProperties = {
  padding: "10px 12px",
  cursor: "pointer",
  borderBottom: "1px solid #0f1827",
};

const itemActiveStyle: React.CSSProperties = {
  background:
    "linear-gradient(180deg, rgba(37,99,235,0.18), rgba(37,99,235,0.08))",
};

const rowTopStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
};

const badgeStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#9db7ff",
  background: "rgba(37,99,235,0.18)",
  border: "1px solid rgba(37,99,235,0.35)",
  padding: "2px 6px",
  borderRadius: 999,
  whiteSpace: "nowrap",
};

const nameStyle: React.CSSProperties = {
  color: "#e5eefb",
  fontWeight: 600,
};

const subLabelStyle: React.CSSProperties = {
  marginTop: 2,
  color: "#9ab0cc",
  fontSize: 12,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const dropdownHintStyle: React.CSSProperties = {
  padding: "12px",
  color: "#9ab0cc",
  fontSize: 13,
};

/* Popular locations */
const popularBoxStyle: React.CSSProperties = {
  marginTop: 10,
};

const popularTitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#9ca3af",
  marginBottom: 4,
};

const popularChipsWrapperStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
};

const popularChipStyle: React.CSSProperties = {
  padding: "5px 10px",
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.7)",
  background: "rgba(15,23,42,0.95)",
  color: "#e5e7eb",
  fontSize: 12,
  cursor: "pointer",
};

/* Sidebar actions */
const sidebarActionsStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  marginTop: 10,
};

/* Main map area */
const mapOuterStyle: React.CSSProperties = {
  width: "100%",
  alignSelf: "stretch",
};

const mapCardStyle: React.CSSProperties = {
  background:
    "linear-gradient(145deg, rgba(4,7,15,0.98), rgba(4,7,15,0.96))",
  borderRadius: 22,
  padding: "14px",
  border: "1px solid rgba(148,163,184,0.35)",
  boxShadow: "0 22px 50px rgba(0,0,0,0.85)",
  position: "relative",
  display: "flex",
  flexDirection: "column",
  height: "100%",
};

/* Map */
const mapWrapperStyle: React.CSSProperties = {
  borderRadius: 18,
  overflow: "hidden",
  boxShadow: "0 12px 30px rgba(0,0,0,0.7)",
  flex: 1,
};

const mapStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  minHeight: 480,
};

/* Buttons */
const confirmButtonStyle: React.CSSProperties = {
  padding: "10px 14px",
  background: "linear-gradient(135deg, #22c55e, #a3e635)",
  color: "#0b1120",
  border: "none",
  borderRadius: 999,
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 14,
};

const currentLocationButtonStyle: React.CSSProperties = {
  padding: "8px 14px",
  fontSize: 13,
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.6)",
  background: "rgba(15,23,42,0.9)",
  color: "#e5e7eb",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

export default ChangeLocationGeo;
